/**
 * `JsonRpcConnection` — the shared, transport-agnostic JSON-RPC 2.0 core
 * (P0b of `docs/cloud-vm/design/crucible-client-sdk-tdd.md` §3.2). Lifts the
 * mechanics both wire clients duplicated — request/response correlation, the
 * `#pending` table, streaming, notification routing, frame codec, and
 * `request.cancel@1`-on-abort — out of `BrowserCrucibleClient` (browser) and
 * `@volley/crucible`'s node `JsonRpcClient`, leaving each as a thin policy
 * layer on top.
 *
 * It is deliberately **policy-free + DOM-free**: it programs against the
 * minimal {@link WebSocketLike} seam, so it carries no reconnect, no session
 * hello/resume, no timeouts, no Electron stubs, no channel↔method mapping, and
 * no observer-chunk routing. Those layer on top per environment. The five
 * seams it DOES expose (so policy can re-implement its behaviour without the
 * core knowing the specifics) are:
 *
 *   1. `mapError` — turn a wire error envelope into the policy's error type
 *      (browser → `CrucibleError`; node → `JsonRpcRemoteError`, whose identity
 *      5 callers `instanceof`-check, so it MUST be preserved by the node
 *      policy). The core never names either type.
 *   2. notification interceptors — run BEFORE the requestId demux, so the node
 *      policy can route observer chunks (observerId+requestId) the way
 *      Hello-TV needs without the core knowing about observers.
 *   3. per-stream `onChunkMeta(seq)` — fires for every stream chunk so policy
 *      can track `lastSeenSeq` (browser resumption) or reset an inactivity
 *      timer (node `streamTimeoutMs`).
 *   4. `mintRequestId` — deterministic ids in tests; `crypto.randomUUID` in
 *      prod (browser Web Crypto + node ≥16 both expose `globalThis.crypto`).
 *   5. `onDiagnostic` — sink for dropped/malformed frames (browser →
 *      `console.warn`; node → `process.stderr`).
 *
 * Connection-level failures (abort, transport-unavailable, socket-closed) are
 * rejected as a neutral {@link RpcConnectionError} tagged by `kind`; the policy
 * layer translates those to its public error type. Only WIRE error envelopes
 * go through `mapError`.
 *
 * Lifecycle is raw: `connect()` opens a FRESH socket each call (abandoning any
 * prior one, retaining resumable data-plane pending) and resolves on the
 * WebSocket `open` event (NOT on any `session.hello@1`). Session negotiation is
 * the policy layer's job, sent via `controlRequest()` from an `onOpen` hook so
 * it goes out synchronously on open. `close()` is caller-initiated. An
 * unexpected close rejects every in-flight request/stream (unless
 * `retainPendingOnUnexpectedClose`) and fires the `onClose` hooks so policy can
 * reconnect. `onOpen` / `onClose` are the two raw socket-lifecycle seams;
 * `rejectRequest` / `rejectAllPending` let policy settle (retained) pending on
 * partial / full session loss.
 *
 * TWO request planes: `request()`/`stream()` are the resumable DATA plane
 * (`#pending` — retained across a transient close, in `inFlightIds()`, settled
 * by `rejectAllPending`). `controlRequest()` is the connection-scoped CONTROL
 * plane (`session.hello@1`/`session.resume@1`) — NOT resumable, NOT in
 * `inFlightIds()`, rejected the moment its socket is replaced or closed. Keeping
 * session negotiation off the data plane is why a drain / session-loss /
 * supersede can't mistake it for resumable work.
 */

import {
  WS_OPEN,
  type AuthAttachment,
  type AuthResolver,
  type WebSocketLike,
  type WebSocketTransportFactory,
} from './websocket-like.js';
import { makeAsyncQueue } from './async-queue.js';

/** A JSON-RPC 2.0 error body as it arrives on the wire (`jsonrpc-channel.md` §2.5). */
export interface RpcErrorEnvelope {
  code: number;
  message: string;
  data?: unknown;
}

/** Why a connection-level (non-wire) failure happened. Policy maps by kind. */
export type RpcErrorKind = 'aborted' | 'transport' | 'closed' | 'superseded';

/**
 * Neutral connection-level error. Distinct from a WIRE error (which goes
 * through `mapError` to the policy's type) — this is raised for aborts,
 * send failures, and socket closes, which have no wire envelope. Policy
 * translates `kind` to its public error type if it cares.
 */
export class RpcConnectionError extends Error {
  readonly kind: RpcErrorKind;
  /**
   * The WebSocket close code, when this error originated from a socket close
   * (a close BEFORE the open event). Lets a policy layer make the SAME
   * code-exact reconnect decision it makes for an after-open close it observes
   * via {@link JsonRpcConnection.onClose} — the core splits the two close paths
   * (before-open rejects `connect()`; after-open fires `onClose`), so without
   * the code the policy couldn't unify them.
   */
  readonly closeCode?: number;
  constructor(
    message: string,
    kind: RpcErrorKind,
    options?: { cause?: unknown; closeCode?: number },
  ) {
    super(message, options);
    this.name = 'RpcConnectionError';
    this.kind = kind;
    this.closeCode = options?.closeCode;
  }
}

/** Streaming handle — the chunks iterable, the terminal `done` promise, and the minted id (for `request.cancel@1`). */
export interface JsonRpcStream<TChunk = unknown, TResult = unknown> {
  chunks: AsyncIterable<TChunk>;
  done: Promise<TResult>;
  requestId: string;
}

/**
 * Returns `true` to claim a notification (routing stops). Registered by policy
 * and consulted BEFORE the in-flight-request demux. The node policy uses this
 * for observer-chunk routing; the browser registers none.
 */
export type NotificationInterceptor = (
  method: string,
  params: Record<string, unknown> | undefined,
) => boolean;

export interface StreamOptions {
  signal?: AbortSignal;
  /**
   * How to terminate the chunk iterable when the stream ends with an ERROR
   * (TDD open Q4): `'throw'` makes iteration throw (browser `invokeStreaming`
   * contract) — `'complete'` ends the chunks cleanly and surfaces the error
   * only via `done` (node `JsonRpcClient.stream` contract). Defaults to
   * `'throw'`. A SUCCESSFUL end always completes the chunks cleanly.
   */
  errorTermination?: 'throw' | 'complete';
  /** Fires for every chunk with the envelope `seq` (if any) — for policy seq-tracking / inactivity timers. */
  onChunkMeta?: (meta: { seq?: number }) => void;
}

export interface JsonRpcConnectionOptions {
  transport: WebSocketTransportFactory;
  url: string;
  /**
   * The credential for each socket open. A fixed {@link AuthAttachment}, OR a sync
   * {@link AuthResolver} thunk resolved fresh inside `connect()` immediately before
   * the transport open (per-connect re-mint, no pre-socket await — D1 §4.1).
   */
  auth: AuthAttachment | AuthResolver;
  /** Map a WIRE error envelope to the policy's error type. Default: a generic `Error`. */
  mapError?: (envelope: RpcErrorEnvelope) => Error;
  /** Request-id minter. Default `globalThis.crypto.randomUUID`. */
  mintRequestId?: () => string;
  /** Sink for dropped/malformed frames. Default: no-op. */
  onDiagnostic?: (message: string) => void;
  /**
   * On an UNEXPECTED close (not a caller `close()`), RETAIN in-flight requests
   * + streams instead of rejecting them — so a policy layer can drive
   * `session.resume@1` (using {@link JsonRpcConnection.inFlightIds}) and let the
   * server replay chunks / settle the originals (the browser resumption model).
   * Default `false`: reject every pending on close (the node model). A caller
   * `close()` ALWAYS rejects, regardless of this flag.
   */
  retainPendingOnUnexpectedClose?: boolean;
}

interface JsonRpcResponse {
  jsonrpc: '2.0';
  id: string | number | null;
  result?: unknown;
  error?: RpcErrorEnvelope;
}

interface JsonRpcNotification {
  jsonrpc: '2.0';
  method: string;
  params?: Record<string, unknown>;
}

interface Pending {
  resolve: (result: unknown) => void;
  reject: (err: Error) => void;
  signal?: AbortSignal;
  abortListener?: () => void;
  /** Set for streaming requests; pushes a chunk payload to the stream's queue. */
  pushChunk?: (chunk: unknown) => void;
  /** Set for streaming requests; ends the chunk queue on terminal settle. */
  endChunks?: (err?: Error) => void;
}

export class JsonRpcConnection {
  #socket: WebSocketLike | null = null;
  /**
   * Reject of the CURRENT in-flight `connect()` attempt's promise, captured so
   * an abandon (a superseding `connect()`) or `close()` can SETTLE a still-
   * CONNECTING attempt instead of leaving its promise to dangle (its late close
   * is ignored by the stale-socket guard). Cleared the moment the attempt
   * settles (open / close-before-open). Null between a successful open and the
   * next connect().
   */
  #pendingConnectReject: ((err: Error) => void) | null = null;
  #closedExplicitly = false;
  #pending = new Map<string, Pending>();
  /**
   * Control-plane requests (session negotiation: `session.hello@1` /
   * `session.resume@1`) — tracked SEPARATELY from the resumable data-plane
   * `#pending`. Connection-scoped: NOT in {@link inFlightIds}, NOT retained
   * across a close, NOT settled by {@link rejectAllPending}; rejected the
   * moment their socket is replaced or closed.
   */
  #controlPending = new Map<
    string,
    { resolve: (result: unknown) => void; reject: (err: Error) => void }
  >();
  #chunkMeta = new Map<string, (meta: { seq?: number }) => void>();
  #subscribers = new Map<string, Set<(params: Record<string, unknown>) => void>>();
  #interceptors = new Set<NotificationInterceptor>();
  #closeHandlers = new Set<(ev: { code: number; reason: string }) => void>();
  #openHandlers = new Set<() => void>();
  /** Target ids of just-cancelled requests — their late terminal Response is expected, so it's dropped without a diagnostic (codex P3). Bounded. */
  #cancelledIds = new Set<string>();

  readonly #transport: WebSocketTransportFactory;
  readonly #url: string;
  readonly #auth: AuthAttachment | AuthResolver;
  readonly #mapError: (e: RpcErrorEnvelope) => Error;
  readonly #mintRequestId: () => string;
  readonly #onDiagnostic: (message: string) => void;
  readonly #retainPendingOnClose: boolean;

  constructor(opts: JsonRpcConnectionOptions) {
    this.#transport = opts.transport;
    this.#url = opts.url;
    this.#auth = opts.auth;
    this.#mapError =
      opts.mapError ??
      ((e) => Object.assign(new Error(e.message), { code: e.code, data: e.data }));
    this.#mintRequestId =
      opts.mintRequestId ?? (() => globalThis.crypto.randomUUID());
    this.#onDiagnostic = opts.onDiagnostic ?? (() => undefined);
    this.#retainPendingOnClose = opts.retainPendingOnUnexpectedClose ?? false;
  }

  /** Ids of every in-flight request/stream — a policy layer's `session.resume@1` payload. */
  inFlightIds(): string[] {
    return [...this.#pending.keys()];
  }

  /** Reject every in-flight request/stream with `err` — a policy layer's session-lost path. */
  rejectAllPending(err: Error): void {
    for (const id of [...this.#pending.keys()]) this.#rejectPending(id, err);
  }

  /**
   * Reject ONE in-flight request/stream by id — a policy layer's PARTIAL
   * session-loss path (the browser's `session.resume@1` reply can name a
   * subset of requestIds the server could not replay; those are rejected
   * while the rest stay pending for the server's replay). No-op if the id
   * isn't pending. Granular sibling of {@link rejectAllPending}.
   */
  rejectRequest(id: string, err: Error): void {
    this.#rejectPending(id, err);
  }

  /**
   * Reject every in-flight CONTROL request (session negotiation) WITHOUT
   * touching the socket — a policy layer's "tear the session down but keep the
   * socket" path (e.g. a bfcache drain, where the browser owns the socket's
   * lifecycle and the app must NOT close it). Rejected as a connection-level
   * `RpcConnectionError` ('closed'), which the policy treats as local teardown
   * (so a late control response can't mutate session state). The socket stays
   * open; a later `connect()` abandons it.
   */
  rejectControlRequests(reason = 'control requests cancelled'): void {
    this.#rejectAllControl(new RpcConnectionError(reason, 'closed'));
  }

  /**
   * Issue a CONTROL-PLANE request (e.g. `session.hello@1` / `session.resume@1`):
   * sent + response-correlated like {@link request}, but tracked SEPARATELY
   * from the resumable data-plane `#pending`. A control request is
   * connection-scoped — NOT in {@link inFlightIds}, NOT retained across a close,
   * NOT settled by {@link rejectAllPending}/{@link rejectRequest}. It is
   * rejected with an `RpcConnectionError` the moment its socket is replaced (a
   * fresh {@link connect}) or closed, and with kind `transport` if the send
   * itself fails. This keeps session negotiation out of the data-plane
   * machinery the resumption model owns, so a drain / session-loss / supersede
   * never mistakes it for resumable work.
   */
  controlRequest<TResult = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ): Promise<TResult> {
    const id = this.#mintRequestId();
    return new Promise<TResult>((resolve, reject) => {
      this.#controlPending.set(id, {
        resolve: resolve as (r: unknown) => void,
        reject,
      });
      try {
        this.#send({ jsonrpc: '2.0', id, method, params });
      } catch (cause) {
        this.#rejectControl(
          id,
          new RpcConnectionError('transport unavailable: send failed', 'transport', { cause }),
        );
      }
    });
  }

  /** True once the socket has opened and not since closed. */
  get isOpen(): boolean {
    return this.#socket?.readyState === WS_OPEN;
  }

  /**
   * Open a FRESH transport and resolve on the raw WebSocket `open` event. Every
   * call abandons any prior live socket (rejecting its connection-scoped
   * control requests; RETAINING resumable data-plane `#pending` for replay on
   * the new socket) and opens a new one with the CURRENT transport/auth — so a
   * concurrent or superseding `connect()` never resolves on a stale socket or a
   * stale credential, and a reconnect after an unexpected close picks up the
   * retained pending. Session negotiation (`session.hello@1`) is the policy
   * layer's job, sent via {@link controlRequest} from an {@link onOpen} hook
   * once the socket opens.
   */
  connect(): Promise<void> {
    this.#closedExplicitly = false;
    this.abandonSocket();
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      this.#pendingConnectReject = reject;
      // D1 §4.1: resolve a per-connect AuthResolver thunk synchronously (no await)
      // immediately before the transport open, so a rotated credential reaches the
      // upgrade on every connect AND reconnect with no supersede window. A fixed
      // AuthAttachment is used as-is.
      const auth =
        typeof this.#auth === 'function' ? this.#auth() : this.#auth;
      const socket = this.#transport(this.#url, auth);
      this.#socket = socket;
      socket.onopen = () => {
        if (settled || this.#socket !== socket) return;
        settled = true;
        this.#pendingConnectReject = null;
        resolve();
        this.#fireOpen();
      };
      socket.onmessage = (data) => {
        if (this.#socket !== socket) return; // ignore a superseded socket's frames
        this.#onFrame(data);
      };
      socket.onerror = () => {
        /* surfaces via onclose */
      };
      socket.onclose = (ev) => {
        // Ignore a close from a socket that has since been superseded by a
        // reconnect / abandoned by a fresh connect(), or torn down via close()
        // (which nulls #socket). Such an event no longer belongs to the live
        // connection and MUST NOT touch global state (control requests /
        // onClose handlers) — guard BEFORE either branch so a stale pre-open
        // close can't reject the new socket's in-flight control request (Codex
        // round-6 P2).
        if (this.#socket !== socket) return;
        if (!settled) {
          settled = true;
          this.#socket = null;
          this.#pendingConnectReject = null;
          // A pre-open socket has no control requests of its own (control is
          // sent from the onOpen hook, after `settled`), so there is nothing to
          // reject here — just reject this attempt's connect() promise.
          reject(
            new RpcConnectionError(
              `socket closed (${ev.code}) before open`,
              ev.code === 4401 || ev.code === 4402 ? 'closed' : 'transport',
              { closeCode: ev.code },
            ),
          );
          return;
        }
        this.#onClose(ev);
      };
    });
  }

  /**
   * Abandon the current socket WITHOUT opening a replacement: reject its
   * connection-scoped control requests, settle a still-CONNECTING attempt's
   * connect() promise (kind `superseded`), RETAIN the resumable data-plane
   * `#pending` (a later {@link connect}/`session.resume@1` replays it — this is
   * NOT {@link close}, which rejects all pending), and close the raw socket (its
   * late close event is ignored — `#socket` has moved on). No-op when there is
   * no live socket (first connect / after an unexpected close already nulled it).
   *
   * Public (crucible-client #1386): a policy layer tears down a SUPERSEDED
   * connect attempt's socket at supersede time — without waiting for the
   * replacement attempt to reach `connect()`, and without `close()`'s
   * reject-all-pending. `connect()` itself uses this to abandon the prior socket
   * before opening a fresh one. `reason` is threaded into the diagnostic errors.
   */
  abandonSocket(reason = 'connect() superseded by a new connect()'): void {
    const socket = this.#socket;
    if (!socket) return;
    this.#socket = null;
    // Settle a still-CONNECTING attempt's connect() promise so a direct caller
    // awaiting it doesn't hang (its late close is ignored by the stale-socket
    // guard). No-op once the attempt has opened (cleared on open). Codex round-7.
    const rejectConnect = this.#pendingConnectReject;
    this.#pendingConnectReject = null;
    rejectConnect?.(new RpcConnectionError(reason, 'superseded'));
    // Same in-flight handling as an unexpected close (Codex round-8): a socket
    // going away rejects connection-scoped control + (unless retained for
    // resumption) the data-plane pending, so a non-resuming caller's request
    // doesn't hang on the abandoned socket.
    this.#rejectInflight(new RpcConnectionError(reason, 'closed'));
    socket.close(1000, 'reconnecting');
  }

  /**
   * Settle the in-flight work tied to a socket that is going away (an
   * unexpected close OR a deliberate {@link abandonSocket}). Connection-scoped
   * control requests are ALWAYS rejected (they can't resume); data-plane
   * `#pending` is RETAINED for the server to replay when the policy opted into
   * resumption (`retainPendingOnUnexpectedClose`), else rejected (the default —
   * the request was sent on a socket that's now gone and won't be replayed).
   * Shared by {@link #onClose} and {@link abandonSocket} so the two teardown
   * paths can't diverge.
   */
  #rejectInflight(err: RpcConnectionError): void {
    this.#rejectAllControl(err);
    if (!this.#retainPendingOnClose) {
      for (const id of [...this.#pending.keys()]) this.#rejectPending(id, err);
    }
  }

  /** Caller-initiated close. Rejects all in-flight data-plane + control requests. */
  async close(code = 1000, reason = 'client closing'): Promise<void> {
    this.#closedExplicitly = true;
    const err = new RpcConnectionError('connection closed by caller', 'closed');
    // Settle an in-flight (pre-open) connect() attempt so a caller awaiting it
    // doesn't hang on the now-ignored close (Codex round-7).
    const rejectConnect = this.#pendingConnectReject;
    this.#pendingConnectReject = null;
    rejectConnect?.(err);
    this.#rejectAllControl(err);
    for (const id of [...this.#pending.keys()]) this.#rejectPending(id, err);
    const socket = this.#socket;
    this.#socket = null;
    socket?.close(code, reason);
  }

  /** Issue a non-streaming request. Rejects with the policy's mapped error on a wire error, or `RpcConnectionError` on abort/transport/close. */
  request<TResult = unknown>(
    method: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<TResult> {
    const id = this.#mintRequestId();
    return this.#dispatch<TResult>(id, method, params, signal);
  }

  /** Issue a streaming request. The `chunks` iterable receives every chunk payload (envelope `requestId`/`seq` stripped); `done` settles on the trailing Response. */
  stream<TChunk = unknown, TResult = unknown>(
    method: string,
    params?: Record<string, unknown>,
    opts: StreamOptions = {},
  ): JsonRpcStream<TChunk, TResult> {
    const id = this.#mintRequestId();
    const queue = makeAsyncQueue<TChunk>();
    const errorTermination = opts.errorTermination ?? 'throw';
    const done = this.#dispatch<TResult>(id, method, params, opts.signal, {
      pushChunk: (chunk) => queue.push(chunk as TChunk),
      endChunks: (err) => {
        if (err && errorTermination === 'throw') queue.fail(err);
        else queue.close();
      },
      onChunkMeta: opts.onChunkMeta,
    });
    // The dispatch promise is `done`; swallow its rejection here only to keep
    // an un-awaited `done` from surfacing as an unhandled rejection — callers
    // still observe it via the returned `done`.
    done.catch(() => undefined);
    return { chunks: queue.iterable, done, requestId: id };
  }

  /** Fire a server-bound notification (no id, no response expected). */
  notify(method: string, params?: Record<string, unknown>): void {
    this.#send({ jsonrpc: '2.0', method, params });
  }

  /** Subscribe to a server-initiated notification method. Returns an unsubscribe fn. */
  on(method: string, handler: (params: Record<string, unknown>) => void): () => void {
    let set = this.#subscribers.get(method);
    if (!set) {
      set = new Set();
      this.#subscribers.set(method, set);
    }
    set.add(handler);
    // codex round-4 P3: capture this subscription's Set + an `unsubscribed`
    // flag so a repeated unsubscribe (or one after the method was
    // re-subscribed with a fresh Set) can't delete a NEWER listener set.
    const captured = set;
    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      captured.delete(handler);
      if (captured.size === 0 && this.#subscribers.get(method) === captured) {
        this.#subscribers.delete(method);
      }
    };
  }

  /** Register a notification interceptor (consulted before the requestId demux). Returns an unregister fn. */
  registerNotificationInterceptor(fn: NotificationInterceptor): () => void {
    this.#interceptors.add(fn);
    return () => this.#interceptors.delete(fn);
  }

  /**
   * Observe socket OPENS (for policy-side session negotiation — the browser
   * sends `session.hello@1` / `session.resume@1` from here so the negotiation
   * frame goes out SYNCHRONOUSLY on the open event, before any caller
   * `request()`). Fires once per successful open, synchronously inside the open
   * event AFTER {@link connect} resolves. Sibling of {@link onClose}: the two
   * raw socket-lifecycle seams the policy layer drives reconnect/resume off.
   * Returns an unregister fn.
   */
  onOpen(handler: () => void): () => void {
    this.#openHandlers.add(handler);
    return () => this.#openHandlers.delete(handler);
  }

  /** Observe unexpected socket closes (for policy-side reconnect). Returns an unregister fn. */
  onClose(handler: (ev: { code: number; reason: string }) => void): () => void {
    this.#closeHandlers.add(handler);
    return () => this.#closeHandlers.delete(handler);
  }

  // ── internals ─────────────────────────────────────────────────────────

  #dispatch<TResult>(
    id: string,
    method: string,
    params: Record<string, unknown> | undefined,
    signal: AbortSignal | undefined,
    stream?: {
      pushChunk: (chunk: unknown) => void;
      endChunks: (err?: Error) => void;
      onChunkMeta?: (meta: { seq?: number }) => void;
    },
  ): Promise<TResult> {
    if (signal?.aborted) {
      stream?.endChunks(undefined); // clean close — abort-before-send isn't an error termination
      return Promise.reject(new RpcConnectionError('request aborted before send', 'aborted'));
    }
    return new Promise<TResult>((resolve, reject) => {
      const abortListener = signal ? () => this.#cancel(id) : undefined;
      this.#pending.set(id, {
        resolve: resolve as (r: unknown) => void,
        reject,
        signal,
        abortListener,
        pushChunk: stream?.pushChunk,
        endChunks: stream?.endChunks,
      });
      if (stream?.onChunkMeta) this.#chunkMeta.set(id, stream.onChunkMeta);
      if (signal && abortListener) {
        signal.addEventListener('abort', abortListener, { once: true });
      }
      try {
        this.#send({ jsonrpc: '2.0', id, method, params });
      } catch (cause) {
        this.#rejectPending(
          id,
          new RpcConnectionError('transport unavailable: send failed', 'transport', { cause }),
        );
      }
    });
  }

  #cancel(id: string): void {
    const pending = this.#pending.get(id);
    if (!pending) return;
    // User-initiated cancel closes the chunk iterable CLEANLY (not as an error
    // termination) before the done-promise rejects, matching BrowserCrucibleClient.
    pending.endChunks?.(undefined);
    pending.endChunks = undefined; // so #rejectPending doesn't re-end it
    // request.cancel@1 MUST be a Request (with an id): the server dispatches
    // only client frames that carry an id and ignores notifications, so a
    // notification cancel would leave the call running on the VM (codex round-3
    // P1 — reverting the round-2 notification mis-fix). We don't act on the
    // cancel's Response, and the target may still emit a late terminal Response
    // — so suppress BOTH ids from the unknown-id diagnostic (codex round-2 P3).
    this.#noteCancelled(id);
    const cancelId = this.#mintRequestId();
    this.#noteCancelled(cancelId);
    try {
      this.#send({ jsonrpc: '2.0', id: cancelId, method: 'request.cancel@1', params: { targetId: id } });
    } catch {
      /* socket gone — server-side state expires on its own timeout */
    }
    this.#rejectPending(id, new RpcConnectionError('request aborted by caller', 'aborted'));
  }

  #send(msg: { jsonrpc: '2.0'; id?: string; method: string; params?: unknown }): void {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WS_OPEN) {
      throw new Error(`JsonRpcConnection.#send: socket readyState=${socket?.readyState ?? 'null'}`);
    }
    socket.send(JSON.stringify(msg));
  }

  #onFrame(raw: string): void {
    if (typeof raw !== 'string' || !raw) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.#onDiagnostic('malformed JSON frame — dropping');
      return;
    }
    // codex P2: frames cross the network boundary — a syntactically-valid but
    // non-object payload (`null` / `42` / `"x"`) would throw `TypeError` on the
    // `in` checks below. Drop + diagnose instead.
    if (typeof parsed !== 'object' || parsed === null) {
      this.#onDiagnostic('non-object JSON frame — dropping');
      return;
    }
    // codex round-4 P2: validate the JSON-RPC envelope before dispatching. A
    // valid object that isn't an envelope (missing `jsonrpc:"2.0"`, or a
    // `params.requestId`-bearing frame with no `method`) must be dropped, not
    // allowed to resolve a pending call or push a stream chunk.
    const env = parsed as Record<string, unknown>;
    if (env.jsonrpc !== '2.0') {
      this.#onDiagnostic('frame missing jsonrpc:"2.0" — dropping');
      return;
    }
    const hasMethod = typeof env.method === 'string';
    const hasId = 'id' in env && env.id !== undefined;
    // A Notification carries `method` and no `id`.
    if (hasMethod && !hasId) {
      this.#onNotification(env as unknown as JsonRpcNotification);
      return;
    }
    // A Response carries `id` and no `method`. JSON-RPC 2.0 §5: `id:null` is a
    // pre-correlation parse error — surface + drop.
    if (hasId && !hasMethod) {
      const response = env as unknown as JsonRpcResponse;
      if (response.id === null) {
        this.#onDiagnostic(
          `server returned id:null error envelope (code=${response.error?.code ?? '?'} message=${response.error?.message ?? '?'})`,
        );
        return;
      }
      const id = String(response.id);
      // Control-plane responses (session negotiation) route to #controlPending
      // FIRST — they're correlated separately from the resumable data plane.
      if (this.#controlPending.has(id)) {
        if ('error' in response && response.error) {
          this.#rejectControl(id, this.#mapError(response.error));
        } else if ('result' in response) {
          this.#resolveControl(id, response.result);
        } else {
          this.#onDiagnostic(`control response for id ${id} carries neither result nor error — rejecting as malformed`);
          this.#rejectControl(id, new RpcConnectionError('malformed response: neither result nor error', 'transport'));
        }
        return;
      }
      if (!this.#pending.has(id)) {
        // codex P3: a late terminal Response for a just-cancelled target is
        // expected — drop it quietly rather than diagnosing it as a server bug.
        if (this.#cancelledIds.delete(id)) return;
        this.#onDiagnostic(`response for unknown id ${id} — dropping (timed-out request or server bug)`);
        return;
      }
      if ('error' in response && response.error) {
        this.#rejectPending(id, this.#mapError(response.error));
      } else if ('result' in response) {
        this.#resolvePending(id, response.result);
      } else {
        // codex P2: a Response with neither result nor error violates JSON-RPC
        // 2.0 §5 — don't resolve the caller with `undefined` (a fake success);
        // reject it as a protocol error.
        this.#onDiagnostic(`response for id ${id} carries neither result nor error — rejecting as malformed`);
        this.#rejectPending(id, new RpcConnectionError('malformed response: neither result nor error', 'transport'));
      }
      return;
    }
    // Both `id` AND `method` (a server-initiated Request — not expected in this
    // direction) OR neither (malformed). Drop + diagnose.
    this.#onDiagnostic('unexpected frame: server-initiated request or missing method/id — dropping');
  }

  #onNotification(notif: JsonRpcNotification): void {
    const params = notif.params;
    // 1. Policy interceptors first (observer-chunk routing lives here).
    for (const intercept of this.#interceptors) {
      try {
        if (intercept(notif.method, params)) return;
      } catch {
        /* an interceptor throw must not crash dispatch */
      }
    }
    // 2. Stream-chunk demux by requestId.
    const requestId = params?.requestId;
    if (typeof requestId === 'string') {
      const pending = this.#pending.get(requestId);
      if (!pending?.pushChunk) {
        this.#onDiagnostic(`stream chunk for unknown requestId ${requestId} — dropping`);
        return;
      }
      const seq = typeof params!.seq === 'number' ? (params!.seq as number) : undefined;
      this.#chunkMeta.get(requestId)?.({ seq });
      // Strip the envelope routing fields; the consumer sees only the payload.
      const { requestId: _r, seq: _s, ...payload } = params as Record<string, unknown>;
      pending.pushChunk(payload);
      return;
    }
    // 3. Plain server-initiated notification → subscribers.
    const subs = this.#subscribers.get(notif.method);
    if (!subs) return;
    for (const handler of subs) {
      try {
        handler(params ?? {});
      } catch {
        /* a subscriber throw must not crash dispatch */
      }
    }
  }

  #fireOpen(): void {
    for (const handler of this.#openHandlers) {
      try {
        handler();
      } catch {
        /* an open handler throw must not crash the open path */
      }
    }
  }

  #onClose(ev: { code: number; reason: string }): void {
    const closed =
      ev.code === 4401 || ev.code === 4402
        ? new RpcConnectionError(`auth failure (close code ${ev.code})`, 'closed')
        : new RpcConnectionError(`connection closed (${ev.code})`, 'closed');
    // Reject connection-scoped control + (unless retained for resumption)
    // data-plane pending — shared with abandonSocket so the two teardown paths
    // stay identical.
    this.#rejectInflight(closed);
    if (this.#socket?.readyState !== WS_OPEN) {
      this.#socket = null;
    }
    if (this.#closedExplicitly) return;
    for (const handler of this.#closeHandlers) {
      try {
        handler(ev);
      } catch {
        /* a close handler throw must not crash dispatch */
      }
    }
  }

  #resolveControl(id: string, result: unknown): void {
    const control = this.#controlPending.get(id);
    if (!control) return;
    this.#controlPending.delete(id);
    control.resolve(result);
  }

  #rejectControl(id: string, err: Error): void {
    const control = this.#controlPending.get(id);
    if (!control) return;
    this.#controlPending.delete(id);
    control.reject(err);
  }

  #rejectAllControl(err: Error): void {
    for (const id of [...this.#controlPending.keys()]) this.#rejectControl(id, err);
  }

  #resolvePending(id: string, result: unknown): void {
    const pending = this.#pending.get(id);
    if (!pending) return;
    this.#pending.delete(id);
    this.#chunkMeta.delete(id);
    this.#detachAbort(pending);
    pending.endChunks?.(undefined); // success → clean close
    pending.resolve(result);
  }

  #rejectPending(id: string, err: Error): void {
    const pending = this.#pending.get(id);
    if (!pending) return;
    this.#pending.delete(id);
    this.#chunkMeta.delete(id);
    this.#detachAbort(pending);
    pending.endChunks?.(err); // error → fail OR close, per the stream's errorTermination
    pending.reject(err);
  }

  #detachAbort(pending: Pending): void {
    if (pending.signal && pending.abortListener) {
      pending.signal.removeEventListener('abort', pending.abortListener);
    }
  }

  /** Remember a cancelled target id so its late terminal Response is dropped quietly. Bounded (evicts oldest). */
  #noteCancelled(id: string): void {
    this.#cancelledIds.add(id);
    if (this.#cancelledIds.size > 1024) {
      const oldest = this.#cancelledIds.values().next().value;
      if (oldest !== undefined) this.#cancelledIds.delete(oldest);
    }
  }
}
